import palette from 'constants/palette'
import type { StyleObject } from 'styletron-standard'

// The Overview's vertical rhythm and the surface tokens its setup section
// shares, measured off the pinned prototype
// (order-feedback-landing-page/v2 v67).
//
// context:
//   The design models each block below the hero as one `.ov-sec`:
//   `padding: 44px 0` with a hairline rule on top. Two consecutive sections
//   therefore sit 88px apart with a divider between them — not the ad-hoc
//   32px/18px margins this page used before, and not borderless. Keeping the
//   values here rather than repeating them per component is what stops the
//   two sections drifting apart again.

// One section block below the hero.
export const OVERVIEW_SECTION_STYLE: StyleObject = {
  padding: '44px 0',
  borderTop: `1px solid ${palette.grey20}`,
}

// A section's own heading. Both visible section headings in the design are
// 22px/700 — the 16px/400 treatment belongs to a different, hidden element
// ("Guest satisfaction"), which is how this page ended up with an
// under-weighted "Your feedback setup".
export const OVERVIEW_SECTION_HEADING_STYLE: StyleObject = {
  fontSize: '22px',
  fontWeight: 700,
  lineHeight: '33px',
  color: palette.grey90,
  margin: '0 0 6px',
}

// Gap between a section heading and the cards beneath it: 38px from the
// heading's last line to the card's top edge, which is what the design
// measures.
//
// context:
//   This is the WHOLE gap, not an increment on top of the heading's own 6px
//   bottom margin. That 6px spaces a heading from a sub-line directly under
//   it ("How it works"); a section whose heading is followed straight by a
//   card has no sub-line, so the consumer neutralises the 6px and carries the
//   full 38px here — see FeedbackSetupSection. Leaving both margins live and
//   adjacent would make the browser COLLAPSE them to the larger of the two
//   rather than sum them: still 38px today, but silently re-snapping to a
//   different number the moment either value moves.
export const OVERVIEW_SECTION_BODY_OFFSET = '38px'

// Gap between the intro line and the grid beneath it ("How it works").
export const OVERVIEW_SECTION_INTRO_GAP = '24px'

// The sub-line under a section heading. The design models this and the
// heading above as one section type, so the pair lives here together rather
// than the heading being shared and the sub being redeclared per call site.
//
// context:
//   `max-width` is the design's own readable-measure cap, authored there as
//   `66ch` and kept in those units so it tracks the font rather than freezing
//   one font's pixel width. Today's copy renders well inside it, so the cap
//   is invisible until copy grows or a translation runs long — which is
//   exactly when an uncapped line would run the section's full width.
export const OVERVIEW_SECTION_SUB_STYLE: StyleObject = {
  fontSize: '16px',
  lineHeight: '24px',
  color: palette.grey70,
  maxWidth: '66ch',
  margin: `0 0 ${OVERVIEW_SECTION_INTRO_GAP}`,
}

// The setup card's own padding, in px.
//
// context:
//   Exported as numbers rather than a string because the promo band inside
//   the card bleeds to its edges by cancelling these exact values with
//   negative margins (see PromoBlock's 'inset' variant). The two are one
//   measurement expressed twice with opposite signs, so they have to move
//   together or the band stops reaching the card's corners.
export const OVERVIEW_SETUP_CARD_PADDING_Y = 26
export const OVERVIEW_SETUP_CARD_PADDING_X = 28

// Three hairlines and a glyph colour the design uses that constants/palette
// has no token for. Named here rather than rounded to a near-miss: grey40
// (#c2c8ce) reads as no border at all against white at 1.5px, and both
// spearmint20 (#D0F3F2) and spearmint30 (#89E0DF) are visibly off the promo's
// rules. The promo's fill itself IS a token — palette.spearmint10 — so it is
// not repeated here.
export const OVERVIEW_CARD_BORDER = '#c3c9cf'
// Rule between the setup card's white half and the promo band inset below it.
export const OVERVIEW_PROMO_INSET_RULE = '#cdeeee'
// Border of the promo where it stands as a card of its own.
export const OVERVIEW_PROMO_CARD_BORDER = '#b7ecec'
// The teal of the promo's check-mark bullets.
export const OVERVIEW_CHECK_COLOR = '#0e8a88'

// A CTA demoted to a text link. The design uses it wherever the action is a
// shortcut to something already set up, and keeps a real button only where
// the action is the page's own turn-it-on affordance.
//
// context:
//   Applied to a <button>, not an <a>: every one of these fires a handler
//   (a tab switch or a route push) rather than resolving an href, so the
//   button element is what carries the right role and keyboard behaviour.
//   The design's own `.btn-text` is an <a> only because the prototype has
//   nowhere to dispatch to.
export const OVERVIEW_TEXT_LINK_STYLE: StyleObject = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '21px',
  color: palette.belizeHole,
  textDecoration: 'none',
  ':hover': { textDecoration: 'underline' },
}
